/**
 * jobBriefApi.js — Axios wrapper around the /api/job-briefing/v2/* endpoints.
 * Keeps components thin and lets the offline layer (Phase F) intercept in one place.
 */
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

function authHeaders() {
  const t = localStorage.getItem('token') || localStorage.getItem('access_token') || '';
  return t ? { Authorization: `Bearer ${t}` } : {};
}

async function _wrap(promise, fallback = null) {
  try {
    const r = await promise;
    return r.data;
  } catch (e) {
    if (fallback !== null) return fallback;
    throw e;
  }
}

export const jobBriefApi = {
  listDrafts(ownerEmail) {
    return _wrap(
      axios.get(`${API}/api/job-briefing/v2/drafts`, { headers: authHeaders(), params: { owner_email: ownerEmail } }),
      { drafts: [] },
    );
  },

  createDraft(payload) {
    return _wrap(axios.post(`${API}/api/job-briefing/v2/drafts`, payload, { headers: authHeaders() }));
  },

  getDraft(id) {
    return _wrap(axios.get(`${API}/api/job-briefing/v2/drafts/${id}`, { headers: authHeaders() }));
  },

  updateDraft(id, updates) {
    return _wrap(axios.put(`${API}/api/job-briefing/v2/drafts/${id}`, updates, { headers: authHeaders() }));
  },

  rebrief(id, { reason, condition_changes = [] }) {
    return _wrap(
      axios.post(`${API}/api/job-briefing/v2/drafts/${id}/rebrief`, { reason, condition_changes }, { headers: authHeaders() }),
    );
  },

  listRevisions(id) {
    return _wrap(axios.get(`${API}/api/job-briefing/v2/drafts/${id}/revisions`, { headers: authHeaders() }), { revisions: [] });
  },

  addTimelineEvent(id, ownerEmail, event) {
    return _wrap(
      axios.post(
        `${API}/api/job-briefing/v2/drafts/${id}/timeline`,
        event,
        { headers: authHeaders(), params: { owner_email: ownerEmail } },
      ),
    );
  },

  getTimeline(id) {
    return _wrap(axios.get(`${API}/api/job-briefing/v2/drafts/${id}/timeline`, { headers: authHeaders() }), { events: [] });
  },

  listKnowledgeSources() {
    return _wrap(axios.get(`${API}/api/job-briefing/v2/knowledge/sources`, { headers: authHeaders() }), { sources: [] });
  },

  lookupKnowledge(topic) {
    return _wrap(
      axios.get(`${API}/api/job-briefing/v2/knowledge/lookup`, { headers: authHeaders(), params: { topic } }),
      { citation: null, fallback: null, topic },
    );
  },

  /* ── Phase B: catalogs & HP recommendations ────────────────────── */
  getHazardCatalog() {
    return _wrap(
      axios.get(`${API}/api/job-briefing/v2/catalogs/hazards`, { headers: authHeaders() }),
      { hazards: [] },
    );
  },

  getPpeCatalog() {
    return _wrap(
      axios.get(`${API}/api/job-briefing/v2/catalogs/ppe`, { headers: authHeaders() }),
      { ppe: [] },
    );
  },

  getErrorTrapCatalog() {
    return _wrap(
      axios.get(`${API}/api/job-briefing/v2/catalogs/error-traps`, { headers: authHeaders() }),
      { error_traps: [] },
    );
  },

  recommendHpTools(errorTraps) {
    return _wrap(
      axios.post(
        `${API}/api/job-briefing/v2/hp-tools/recommend`,
        { error_traps: errorTraps || [] },
        { headers: authHeaders() },
      ),
      { recommended: [], fallback_message: null },
    );
  },

  /* ── Phase C: Perform-phase state + event logger ──────────────────── */
  getPerformState(draftId) {
    return _wrap(
      axios.get(`${API}/api/job-briefing/v2/drafts/${draftId}/perform/state`, { headers: authHeaders() }),
      {
        hp_tool_uses: [],
        place_keeping: { procedure_name: null, steps: [], completed_step_indices: [], current_step_index: null, total_steps: 0 },
        three_way_comms: [],
        counts: { hp_tool_uses: 0, place_keeping_steps: 0, three_way_comms: 0 },
      },
    );
  },

  logPerformEvent(draftId, ownerEmail, eventType, payload = {}, jobStep = null, notes = null) {
    return _wrap(
      axios.post(
        `${API}/api/job-briefing/v2/drafts/${draftId}/timeline?owner_email=${encodeURIComponent(ownerEmail)}`,
        { event_type: eventType, payload, job_step: jobStep, notes },
        { headers: authHeaders() },
      ),
      null,
    );
  },

  /* ── Phase D: holds, conditions-changed, attachments ──────────────── */
  createHold(draftId, { reason, step_index, step_name, created_by }) {
    return _wrap(
      axios.post(`${API}/api/job-briefing/v2/drafts/${draftId}/perform/hold`,
        { reason, step_index, step_name, created_by },
        { headers: authHeaders() }),
      { hold_id: null },
    );
  },

  releaseHold(draftId, holdId, { released_by, note }) {
    return _wrap(
      axios.post(`${API}/api/job-briefing/v2/drafts/${draftId}/perform/hold/${holdId}/release`,
        { released_by, note },
        { headers: authHeaders() }),
      { released: false },
    );
  },

  flagConditionsChanged(draftId, { what_changed, flagged_by }) {
    return _wrap(
      axios.post(`${API}/api/job-briefing/v2/drafts/${draftId}/perform/conditions-changed`,
        { what_changed, flagged_by },
        { headers: authHeaders() }),
      { new_revision: null },
    );
  },

  uploadAttachment(draftId, { filename, mime_type, data_base64, caption, step_index, uploaded_by }) {
    return _wrap(
      axios.post(`${API}/api/job-briefing/v2/drafts/${draftId}/perform/attachment`,
        { filename, mime_type, data_base64, caption, step_index, uploaded_by },
        { headers: authHeaders() }),
      { attachment_id: null },
    );
  },

  getAttachment(draftId, attachmentId) {
    return _wrap(
      axios.get(`${API}/api/job-briefing/v2/drafts/${draftId}/perform/attachment/${attachmentId}`,
        { headers: authHeaders() }),
      null,
    );
  },

  /* ── Phase E: Close-Out ────────────────────────────────────────── */
  getRestorationCatalog() {
    return _wrap(
      axios.get(`${API}/api/job-briefing/v2/catalogs/restoration`, { headers: authHeaders() }),
      { restoration: [] },
    );
  },

  getCloseoutState(draftId) {
    return _wrap(
      axios.get(`${API}/api/job-briefing/v2/drafts/${draftId}/closeout/state`, { headers: authHeaders() }),
      { restoration: [], lessons_learned: [], signoffs: [], completed_at: null,
        counts: { restoration_total: 0, restoration_completed: 0, lessons: 0, signoffs: 0 } },
    );
  },

  addRestorationItem(draftId, payload) {
    return _wrap(
      axios.post(`${API}/api/job-briefing/v2/drafts/${draftId}/closeout/restoration`,
        payload, { headers: authHeaders() }),
      null,
    );
  },

  checkRestorationItem(draftId, itemId, payload = {}) {
    return _wrap(
      axios.post(`${API}/api/job-briefing/v2/drafts/${draftId}/closeout/restoration/${itemId}/check`,
        payload, { headers: authHeaders() }),
      null,
    );
  },

  uncheckRestorationItem(draftId, itemId) {
    return _wrap(
      axios.post(`${API}/api/job-briefing/v2/drafts/${draftId}/closeout/restoration/${itemId}/uncheck`,
        {}, { headers: authHeaders() }),
      null,
    );
  },

  removeRestorationItem(draftId, itemId) {
    return _wrap(
      axios.post(`${API}/api/job-briefing/v2/drafts/${draftId}/closeout/restoration/${itemId}/remove`,
        {}, { headers: authHeaders() }),
      null,
    );
  },

  addLessonLearned(draftId, payload) {
    return _wrap(
      axios.post(`${API}/api/job-briefing/v2/drafts/${draftId}/closeout/lessons-learned`,
        payload, { headers: authHeaders() }),
      null,
    );
  },

  addSignoff(draftId, payload) {
    return _wrap(
      axios.post(`${API}/api/job-briefing/v2/drafts/${draftId}/closeout/signoff`,
        payload, { headers: authHeaders() }),
      null,
    );
  },

  completeCloseout(draftId) {
    return _wrap(
      axios.post(`${API}/api/job-briefing/v2/drafts/${draftId}/closeout/complete`,
        {}, { headers: authHeaders() }),
      null,
    );
  },

  getCloseoutPdfUrl(draftId) {
    // Direct URL — the frontend uses window.open or an <a href download>.
    return `${API}/api/job-briefing/v2/drafts/${draftId}/closeout/pdf`;
  },
};
